/**
 * Walker — the generic ground patroller (Brief 008 archetype spine).
 *
 * One class, many species via `props.variant` (snail / mouse / spiny / …), mirroring
 * the FLYER pattern. GOOMBA is DELIBERATELY left as its own class (Goomba.ts) so the
 * locked test01/test02 feel never regresses; Walker is the new generic everything-else.
 *
 * Per-species tuning is all props:
 *   variant      — sprite-key prefix; renders `<variant>_walk_1/2/rest/flat`.
 *   speed        — px/s horizontal patrol speed.
 *   spiked       — true → UN-STOMPABLE: a stomp hurts the player too (spiny).
 *   turnAtLedge  — true (default) reverses at a ledge edge (no ground ahead); false
 *                  walks off ledges (walls-only turning, the classic goomba behaviour).
 *
 * Combat (non-spiked): stomp from above kills + bounces (no heart cost); any other
 * contact costs a heart. Spiked: ALL contact costs a heart, never killed by a stomp.
 *
 * Determinism: floor/abs/min/max only (ESLint-clean under src/engine/). No transcendentals.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

const DEFAULT_SPEED = 60;
const GRAVITY = 2050;
const TERMINAL = 1200;
/** Seconds the flattened sprite lingers after a stomp before going invisible. */
const SQUASH_SECONDS = 0.4;

type Variant = string;

export class Walker implements Entity {
  readonly type = EntityType.WALKER;
  body: AABB;
  vx: number;
  vy = 0;
  facing: 1 | -1 = -1;
  readonly spriteFacesLeft = true; // Kenney enemy art faces left
  alive = true;
  squashTimer = 0;

  private variant: Variant;
  private speed: number;
  private spiked: boolean;
  private turnAtLedge: boolean;
  private walkPhase = 0;

  /**
   * @param cx     spawn center-x (world px)
   * @param feetY  spawn feet-bottom Y (world px)
   * @param props  variant / speed / spiked / turnAtLedge / dir (initial facing)
   */
  constructor(cx: number, feetY: number, props?: Record<string, unknown>, w = 52, h = 48) {
    this.body = { x: cx - w / 2, y: feetY - h, w, h };

    const variant = props?.variant;
    this.variant = typeof variant === 'string' && variant.length ? variant : 'snail';
    this.speed = typeof props?.speed === 'number' ? props.speed : DEFAULT_SPEED;
    this.spiked = props?.spiked === true;
    // turnAtLedge defaults TRUE (safe for arbitrary editor levels) unless explicitly false.
    this.turnAtLedge = props?.turnAtLedge !== false;

    const dir = props?.dir;
    if (dir === 1 || dir === '1' || dir === 'right') this.facing = 1;
    else if (dir === -1 || dir === '-1' || dir === 'left') this.facing = -1;
    this.vx = this.facing * this.speed;
  }

  private get walkFrameA(): boolean { return Math.floor(this.walkPhase / 40) % 2 === 0; }

  update(dt: number, ctx: EntityContext): void {
    if (!this.alive) { this.squashTimer = Math.max(0, this.squashTimer - dt); return; }
    const grid = ctx.grid;

    // gravity + ground
    this.vy = Math.min(this.vy + GRAVITY * dt, TERMINAL);
    if (grid.collideY(this.body, this.vy * dt, false).grounded) this.vy = 0;

    // horizontal patrol; reverse at wall
    if (grid.collideX(this.body, this.vx * dt)) this.reverse();

    // ledge check (optional): reverse if no ground just ahead-below the leading foot
    if (this.turnAtLedge) {
      const aheadX = this.facing > 0 ? this.body.x + this.body.w + 2 : this.body.x - 2;
      const belowY = this.body.y + this.body.h + 4;
      if (!grid.solidBelowPoint(aheadX, belowY)) this.reverse();
    }

    this.walkPhase += Math.abs(this.vx) * dt;
  }

  onPlayerOverlap(ctx: EntityContext): void {
    if (this.spiked) { ctx.hurtPlayer(); return; } // un-stompable: any contact hurts
    const p = ctx.player.body;
    const playerFeet = p.y + p.h;
    // Stomp only with offense (CLASSIC). PRECISION (no offense) → would-be stomp hurts.
    const fallingOnto = ctx.player.vy > 0 && playerFeet - this.body.y < this.body.h * 0.7;
    if (fallingOnto && ctx.offenseEnabled) {
      ctx.killEntity(this);
      ctx.bouncePlayer();
      ctx.playSound('stomp', 0.5); // POLISH_01 #1 — dedicated stomp clip
    } else {
      ctx.hurtPlayer();
    }
  }

  onKilled(): void {
    if (!this.alive && this.squashTimer > 0) return;
    this.alive = false;
    this.squashTimer = SQUASH_SECONDS;
    this.vx = 0;
  }

  /** COMBAT: a spiked WALKER is un-stompable, so the sword/pogo/dash can't kill it either
   *  (kill set == stomp set, Isaac 2026-06-30). Non-spiked → killable like a stomp. */
  damageable(): boolean { return !this.spiked; }

  spriteKey(): string {
    if (!this.alive) return `${this.variant}_flat`;
    if (Math.abs(this.vx) > 1) return this.walkFrameA ? `${this.variant}_walk_1` : `${this.variant}_walk_2`;
    return `${this.variant}_rest`;
  }

  visible(): boolean { return this.alive || this.squashTimer > 0; }

  private reverse(): void {
    this.facing = this.facing > 0 ? -1 : 1;
    this.vx = this.facing * this.speed;
  }
}
